import React, { useEffect, useRef, useState } from 'react';
import { ActionIcon, createStyles, Group, Modal, Textarea } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconArrowRight } from '@tabler/icons-react';
import SergeantIcon from '../../../assets/sergeant.png';
import { makeSafeRequest } from '../../../common/makeSafeRequest';
import { ChatbotOrigin } from '../../../types/chatbot';
import Preloader from '../Preloader';
import { Message, MessageType } from './Message';
import { validateJson } from './validation/validator';

interface IChatbot {
	settings: any;
	origin: ChatbotOrigin;
	onApply: (json: string) => void;
}

type ChatbotResponse = {
	status: string;
	response_text: string;
	response_json: any;
	response_type: string;
	logs_url?: string;
};

const firstMessage: Record<ChatbotOrigin, string> = {
	'force-package': 'How can I help you with this force package?',
	system: 'How can I help you with this system?',
	emitter: 'How can I help you with this emitter?',
	effector: 'How can I help you with this effector?',
	sensor: 'How can I help you with this sensor?',
	platform: 'How can I help you with this platform?',
	general: 'How can I help you?',
	assessment: 'How can I help you with this assessment?',
	scenario: 'How can I help you with this scenario?',
};

export const Chatbot: React.FC<IChatbot> = ({ settings, origin, onApply }) => {
	const { classes } = useStyles();
	const [opened, { open, close }] = useDisclosure(false);
	const [messages, setMessages] = useState<MessageType[]>([
		{ text_message: firstMessage[origin], json_message: undefined },
	]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const promptRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		// Reset chat history when new chatbot is opened (or page is refreshed)
		makeSafeRequest('chatbot/reset-chat', 'POST');
	}, []);

	const onMessageSubmit = async (message: string) => {
		if (message && message.length > 0) {
			setIsLoading(true);
			setMessages((prev) => [...prev, { text_message: message }]);

			const currentJson = settings;

			const json = await makeSafeRequest<ChatbotResponse>('chatbot/ask', 'POST', {
				payload: { message, origin, currentJson },
				onSuccess: () => {
					setIsLoading(false);
				},
				onError: () => {
					setIsLoading(false);
				},
			});

			if (json && json.status === 'ok') {
				const isValid = validateJson(json.response_json, origin);
				setMessages((prev) => [
					...prev,
					{
						text_message: json.response_text,
						json_message: json.response_json
							? JSON.stringify(json.response_json, null, 2)
							: null,
						isValid,
					},
				]);
			} else if (json && json.response_text) {
				setMessages((prev) => [
					...prev,
					{
						text_message: json.response_text,
						json_message: undefined,
						isValid: false,
						logsUrl: json?.logs_url,
					},
				]);
			}
		}
	};

	return (
		<>
			<Modal
				opened={opened}
				onClose={close}
				withCloseButton={false}
				className={classes.modal}
			>
				{messages.map((message, index) => (
					<Message
						key={index}
						message={message}
						sender={index % 2 === 0 ? 'ai' : 'user'}
						onApply={(json) => {
							onApply(json);
							close();
						}}
					/>
				))}

				{isLoading && <Preloader customStyles="justify-content: flex-start;" />}

				<Textarea
					ref={promptRef}
					className={classes.textInput}
					placeholder="Prompt here"
					mt={'1rem'}
					rightSection={
						<ActionIcon
							onClick={() => {
								onMessageSubmit(promptRef.current?.value || '');
								promptRef.current!.value = '';
							}}
						>
							<IconArrowRight></IconArrowRight>
						</ActionIcon>
					}
				></Textarea>
			</Modal>

			<Group position="center">
				<img
					src={SergeantIcon as string}
					className={classes.icon}
					onClick={open}
					alt="ai-chat-sergeant-icon"
				/>
			</Group>
		</>
	);
};

const useStyles = createStyles((theme) => ({
	modal: {
		'& .mantine-Modal-body': {
			backgroundColor:
				theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.light[0],
			display: 'flex',
			flexDirection: 'column',
			gap: '1rem',
			padding: '1rem !important',
		},

		'& .mantine-Modal-inner': {
			paddingLeft: '15vw',
			paddingRight: '15vw',
		},

		'& .mantine-Modal-content': {
			flexGrow: 1,
		},
	},

	icon: {
		width: '2rem',
		height: 'auto',
		cursor: 'pointer',
		transition: 'transform 0.2s',

		'&:hover': {
			transform: 'scale(0.90)',
		},
	},

	textInput: {
		'& .mantine-Input-input:focus': {
			border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.light[4]}`,
		},
	},
}));
